/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-duplicate-props */
import React, { useState, ReactElement, useContext, useEffect } from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import {
  TextField,
  DialogActions,
  Grid,
  Typography,
  LinearProgress,
  Box,
  Button,
} from '@material-ui/core';
import moment, { Moment } from 'moment';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  MessageFormFields,
  Action,
  MessageFormikSubmit,
  MessageSchemaValidation,
  ImageAspectRatio,
  UploadedImage,
} from 'models/forms';
import { NotificationContext } from 'context/NotificationContext';
import { Message } from 'models';
import { Timestamp } from 'models/firebase';
import { timestampToDate, isSameDay } from 'helpers/date';
import {
  CANCEL_MESSAGE_CONFIRMATION_TITLE,
  CANCEL_MESSAGE_CONFIRMATION_BODY,
  REMOVE_MESSAGE_CONFIRMATION_TITLE,
  REMOVE_MESSAGE_CONFIRMATION_BODY,
} from 'constants/notifications';
import DatePicker from 'components/form/DatePicker';
import ImageUploader from 'components/form/ImageUploader';
import SecondaryButton from 'components/form/buttons/SecondaryButton';
import PrimaryButton from 'components/form/buttons/PrimaryButton';
import useMessages from 'hooks/useMessages';
import ConfirmationDialog from 'components/mainComponents/ConfirmationDialog';
import TextFieldLength from 'components/form/TextFieldLength';
import SubmitLoading from 'components/loading/SubmitLoading';
import ImagePreview from 'components/form/ImagePreview';
import { getMessagesFormNotification } from 'helpers/notifications';
import { MESSAGES } from 'constants/routes';
import useStyles from './style';

interface Props {
  message?: Message;
}

const AddNewMessageForm: React.FC<Props> = (props) => {
  const { message } = props;
  const isEdit = message !== undefined;
  const initialValues = { date: '', text: message?.text || '' };
  const editDate = message?.date
    ? timestampToDate(message.date as Timestamp)
    : null;
  const classes = useStyles();
  const history = useHistory();
  const { displayNotification } = useContext(NotificationContext);
  const { add, getDates, remove, update } = useMessages();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(editDate as null | Date);
  const [selectedImage, setSelectedImage] = useState(null as null | Blob);
  const [selectedImageUrl, setSelectedImageUrl] = useState(
    message?.imageURL as null | string,
  );
  const [dates, setDates] = useState([] as Moment[]);
  const [isDateUnavailable, setIsDateUnavailable] = useState(false);
  const [text, setText] = useState(initialValues.text);

  // Initial data load.
  const getUnavailableDates = async (): Promise<void> => {
    const currentDates = await getDates();
    setDates(currentDates);
  };

  // Cancel form actions.
  const handleCancelConfirm = (): void => {
    history.push(MESSAGES, { goBack: true });
  };

  const handleCancelDismiss = (): void => {
    setIsCancelDialogOpen(false);
  };

  // Remove form actions.
  const handleRemoveConfirm = async (): Promise<void> => {
    setIsRemoveDialogOpen(false);
    setIsProcessing(true);
    if (message?.id) {
      const removed = await remove(message.id, message.imageURL);
      setIsProcessing(false);
      const notification = getMessagesFormNotification(removed, Action.REMOVE);
      displayNotification(notification.status, notification.message);
      history.goBack();
    }
  };

  const handleRemoveDismiss = (): void => {
    setIsRemoveDialogOpen(false);
  };

  // Validations.
  const handleSchemaValidation = (): MessageSchemaValidation => {
    return Yup.object().shape({
      date: Yup.date(),
    });
  };

  const isSaveDissabled = (isSubmitting: boolean): boolean => {
    return (
      isSubmitting ||
      !selectedDate ||
      (!isEdit && !selectedImage) ||
      (isEdit && !message?.imageURL)
    );
  };

  // Form actions.
  const handleCancel = (value: string): void => {
    if (value !== '' || selectedDate || selectedImage) {
      setIsCancelDialogOpen(true);
    } else {
      history.push(MESSAGES, { goBack: true });
    }
  };

  const handleFormSubmit = async (
    values: MessageFormFields,
    helpers: MessageFormikSubmit,
  ): Promise<void> => {
    let action = Action.ADD;
    let result = false;
    helpers.setSubmitting(true);
    setIsProcessing(true);
    const newMessage = {
      date: selectedDate as Date,
      text: values.text,
      imageURL: selectedImage as Blob,
    };
    if (message) {
      result = await update(message, newMessage);
      action = Action.UPDATE;
    } else {
      result = await add(newMessage);
    }
    const notification = getMessagesFormNotification(result, action);
    displayNotification(notification.status, notification.message);
    history.push(MESSAGES, { goBack: action === Action.UPDATE });
  };

  const handleRemove = (): void => {
    setIsRemoveDialogOpen(true);
  };

  // Data updates handlers.
  const handleImageUpload = (image: UploadedImage): void => {
    setSelectedImage(image.file);
    setSelectedImageUrl(image.url);
  };

  const handleDateChange = async (date: Date | null): Promise<void> => {
    if (date) {
      const isCurrentDate = message && isSameDay(message.date, date);
      const filteredDates = dates.filter((currentDate) => {
        return moment(currentDate).isSame(date, 'day');
      });
      if (filteredDates.length === 0 || isCurrentDate) {
        setIsDateUnavailable(false);
        setSelectedDate(date);
      } else {
        setIsDateUnavailable(true);
        setSelectedDate(null);
      }
    }
  };

  const handleTextUpdate = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ): void => {
    setText(event.target.value);
  };

  useEffect(() => {
    getUnavailableDates();
  }, []);

  return (
    <>
      <SubmitLoading isOpen={isProcessing} />
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={handleSchemaValidation}
      >
        {(formProps): ReactElement => {
          const {
            values,
            touched,
            errors,
            isSubmitting,
            handleChange,
            handleSubmit,
          } = formProps;
          return (
            <form
              className={clsx(classes.content, {
                [classes.overlay]: isSubmitting || isProcessing,
              })}
              onSubmit={handleSubmit}
            >
              <Typography variant='body2' className={classes.subTitle}>
                {'Date '}
                {isEdit ? '(not editable)' : ''}
              </Typography>
              <DatePicker
                required={!isEdit}
                disablePast
                name='date'
                value={selectedDate}
                onChange={handleDateChange}
                InputProps={{
                  disableUnderline: true,
                  className: classes.datePickerProps,
                }}
                error={isDateUnavailable}
                helperText={
                  isDateUnavailable ? 'Inspiration date already taken.' : ''
                }
              />
              <Grid container className={classes.formContentBottom} spacing={3}>
                <Grid item>
                  <Typography variant='body2' className={classes.subTitle}>
                    Image
                  </Typography>
                  <ImageUploader
                    imageURL={message?.imageURL}
                    aspectRatio={ImageAspectRatio.AR_9_16}
                    onChange={handleImageUpload}
                  />
                  <Typography variant='body2' className={classes.maxTextLength}>
                    Min. image size 800x1731
                  </Typography>
                </Grid>
                <Grid item className={classes.textContainer}>
                  <div className={classes.textContent}>
                    <Typography variant='body2' className={classes.subTitle}>
                      Text
                    </Typography>
                    <TextField
                      multiline
                      rows={20}
                      id='text'
                      name='text'
                      value={values.text}
                      helperText={errors.text && touched.text && errors.text}
                      className={classes.textField}
                      inputProps={{ maxLength: 800 }}
                      onChange={(event): void => {
                        handleChange(event);
                        handleTextUpdate(event);
                      }}
                      InputProps={{
                        disableUnderline: true,
                        className: classes.inputProps,
                      }}
                    />
                    <TextFieldLength text={values.text} maxLength={800} />
                  </div>
                </Grid>
                <Grid item>
                  <Typography variant='body2' className={classes.subTitle}>
                    Preview
                  </Typography>
                  <ImagePreview
                    image={selectedImageUrl}
                    text={text}
                    aspectRatio={ImageAspectRatio.AR_9_16}
                  />
                </Grid>
              </Grid>
              <DialogActions
                className={clsx(classes.dialogActions, {
                  [classes.editActions]: isEdit,
                })}
              >
                {isEdit && (
                  <Button
                    color='primary'
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleRemove}
                    className={classes.deleteButton}
                  >
                    Delete Inspiration
                  </Button>
                )}
                <Box>
                  <SecondaryButton
                    onClick={(): void => handleCancel(values.text)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    type='submit'
                    onClick={handleSubmit}
                    disabled={isSaveDissabled(isSubmitting)}
                  >
                    Save
                  </PrimaryButton>
                </Box>
              </DialogActions>
              {(isSubmitting || isProcessing) && (
                <LinearProgress className={classes.progress} />
              )}
            </form>
          );
        }}
      </Formik>
      <ConfirmationDialog
        isOpen={isCancelDialogOpen}
        title={CANCEL_MESSAGE_CONFIRMATION_TITLE}
        message={CANCEL_MESSAGE_CONFIRMATION_BODY}
        dismissButtonText='Nevermind'
        confirmButtonText='Cancel'
        dismissButtonAction={handleCancelDismiss}
        confirmButtonAction={handleCancelConfirm}
      />
      <ConfirmationDialog
        isOpen={isRemoveDialogOpen}
        title={REMOVE_MESSAGE_CONFIRMATION_TITLE}
        message={REMOVE_MESSAGE_CONFIRMATION_BODY}
        dismissButtonText='Nevermind'
        confirmButtonText='Delete'
        dismissButtonAction={handleRemoveDismiss}
        confirmButtonAction={handleRemoveConfirm}
      />
    </>
  );
};

export default AddNewMessageForm;
